import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import classNames from 'classnames';
import { uniqueId, upperFirst } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { withNamespaces } from 'react-i18next';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import Checkbox from './Checkbox';
import { getCurrentPage } from '../../../redux/reducers/WizardReducer';
import * as WizardActions from '../../../redux/actions/WizardActions';
import * as Yup from 'yup';

class Wizard extends Component {
  static Page = (page) => {
    const { children, values } = page;
    return children && React.cloneElement(children, { values });
  };

  _formData = null;

  constructor(props) {
    super(props);
    this.state = {
      shouldSave: false,
      pageIndex: 0,
    };
  }

  componentDidMount() {
    const { setTotalPagesAction, goToPageAction, children } = this.props;
    goToPageAction(0);
    setTotalPagesAction(children.length);
  }

  wizardInfo = () => {
    const { children, page } = this.props;
    const lastPage = React.Children.count(children) - 1;
    const indexSubmitPage = children.findIndex((child, pageIndex) => {
      const { isSubmitPage } = child.props;
      return isSubmitPage || lastPage === pageIndex;
    });

    return {
      lastPage,
      indexSubmitPage,
      submitPageIsLastPage: lastPage === indexSubmitPage,
      activePage: React.Children.toArray(children)[page],
      isLastPage: page === lastPage,
    };
  };

  onPageHeaderClick = (pageIndex, page, enabled) => {
    if (pageIndex !== page && enabled) {
      this.setShouldSave(true, pageIndex, true);
    }
  };

  renderPageHeaderItem = (child, pageIndex) => {
    const { page } = this.props;
    const { indexSubmitPage, lastPage, submitPageIsLastPage } = this.wizardInfo();

    const { label, icon } = child.props;
    const enabled = submitPageIsLastPage || (page === lastPage ? page === pageIndex : pageIndex <= indexSubmitPage);
    const itemClasses = classNames('wizard-header-item', {
      'wizard-header-item-active': page === pageIndex,
      disabled: !enabled,
    });

    return (
      <li className={itemClasses} key={uniqueId()}>
        <div onClick={() => this.onPageHeaderClick(pageIndex, page, enabled)}>
          {icon && (
            <div className="wizard-header-item-icon">
              <FontAwesomeIcon icon={icon} />
            </div>
          )}
          <div className="wizard-header-item-text">{label}</div>
        </div>
      </li>
    );
  };

  renderPageHeader = () => {
    const { children } = this.props;
    return <ol className="wizard-header">{children.map((child, pageIndex) => this.renderPageHeaderItem(child, pageIndex))}</ol>;
  };

  handleSubmit = async (values, bag) => {
    const { page, goToNextStepAction, onSubmit } = this.props;
    const { shouldSave, pageIndex } = this.state;
    const { isLastPage, activePage, indexSubmitPage } = this.wizardInfo();
    const { isSubmitPage = indexSubmitPage === page, onConfirm } = activePage.props;
    const { createAnother } = values;
    const inSubmitPage = isSubmitPage || onConfirm;

    if (inSubmitPage) {
      const fn = onConfirm || onSubmit;
      const requestStatus = await fn(values, bag);
      bag.setSubmitting(false);
      if (!requestStatus) return;
    }

    if (isLastPage) {
      this.restartNow(createAnother);
      return;
    }

    bag.setTouched({});
    bag.setSubmitting(false);
    goToNextStepAction();
    this.onPageChangeEvent();
    if (!inSubmitPage && shouldSave && page < pageIndex - 1) bag.submitForm();
  };

  setShouldSave = async (shouldSave, index, submit = false) => {
    const { lastPage, activePage } = this.wizardInfo();
    const { onConfirm } = activePage.props;
    const { goToPageAction, page } = this.props;
    const idx = !shouldSave || onConfirm ? page : lastPage;
    const pageIndex = index > -1 ? index : idx;

    this.setState({
      shouldSave,
      pageIndex,
    });

    if (pageIndex < page) {
      await goToPageAction(pageIndex);
      this.onPageChangeEvent();
    } else if (submit) this._formData.submitForm();
  };

  goToPrevStep = async () => {
    const { goToPrevStepAction } = this.props;
    await goToPrevStepAction();
    this.onPageChangeEvent();
  };

  onPageChangeEvent = () => {
    const { page, onPageChange = () => {} } = this.props;

    onPageChange({
      page,
      ...this.wizardInfo(),
    });
  };

  restartNow = async (createAnother = false) => {
    const { cancelRequest = () => {}, goToPageAction } = this.props;
    const { _formData } = this;
    if (createAnother) {
      _formData.resetForm();
      this.setShouldSave(false, 0);
    } else {
      cancelRequest();
      goToPageAction(0);
    }
  };

  render() {
    const { activePage, isLastPage, indexSubmitPage, lastPage } = this.wizardInfo();

    const { t, children, cancelRequest, initialValues, enableReinitialize = false, page, resumeCheckbox = () => {}, autoSave = false } = this.props;

    if (!children || !activePage || !activePage.props) return null;

    const {
      validationSchema = Yup.object().shape({}),
      onConfirm,
      submitButtonText = t('buttonConfirm'),
      submitButtonIcon = 'save',
      buttonNextText = t('buttonNext'),
      buttonNextIcon = 'angle-double-right',
      buttonCancelText = t('buttonCancel'),
      useNextToSubmit = false,
    } = activePage.props;
    const inSubmitPage = indexSubmitPage === page || onConfirm;

    return (
      <div className="wizard">
        {this.renderPageHeader()}
        <Formik initialValues={initialValues} enableReinitialize={enableReinitialize} validationSchema={validationSchema} onSubmit={this.handleSubmit}>
          {(formData) => {
            if (this._formData === null) this._formData = formData;
            const { values, isSubmitting } = formData;
            const { createAnother } = values;
            const checkboxs = resumeCheckbox(values);
            const useNextToCreateAnother = isLastPage && !inSubmitPage && createAnother;
            const buttonNextIsRequired = useNextToSubmit || useNextToCreateAnother;
            return (
              <Form>
                {activePage.props.children && React.cloneElement(activePage, { values })}
                <div className="buttons-container">
                  {Object.keys(checkboxs).map((fieldCheckBox) => {
                    const { checkboxPage = lastPage } = checkboxs[fieldCheckBox];
                    if (checkboxPage !== page) return null;
                    return <Field component={Checkbox} name={fieldCheckBox} key={fieldCheckBox} label={t(`label${upperFirst(fieldCheckBox)}`)} {...checkboxs[fieldCheckBox]} />;
                  })}
                  {cancelRequest &&
                    (page === 0 || isLastPage || inSubmitPage) && ( // cancel
                      <div className="btn btn-ghost" onClick={cancelRequest}>
                        {buttonCancelText}
                      </div>
                    )}
                  {page > 0 &&
                    (!isLastPage || inSubmitPage) && ( // prev
                      <button type="button" className="btn btn-ghost" onClick={this.goToPrevStep}>
                        <FontAwesomeIcon className="mr-2" icon={{ iconName: 'angle-double-left', prefix: 'far' }} />
                        {t('buttonPrevious')}
                      </button>
                    )}
                  {((!inSubmitPage && !isLastPage) || buttonNextIsRequired) && ( // next
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting} onClick={() => this.setShouldSave(useNextToSubmit)}>
                      {buttonNextText}
                      {isSubmitting && useNextToSubmit ? (
                        <FontAwesomeIcon icon="spinner" size="lg" spin className="mx-2" />
                      ) : (
                        <FontAwesomeIcon className="ml-2" icon={{ iconName: buttonNextIcon, prefix: 'far' }} />
                      )}
                    </button>
                  )}
                  {!buttonNextIsRequired &&
                    (inSubmitPage || autoSave) && ( // submit
                      <button type="submit" className="btn btn-primary" disabled={isSubmitting} onClick={() => this.setShouldSave(true)}>
                        {isSubmitting ? (
                          <FontAwesomeIcon icon="spinner" size="lg" spin className="mx-2" />
                        ) : (
                          <FontAwesomeIcon className="mr-2" icon={{ iconName: submitButtonIcon, prefix: 'fal' }} />
                        )}
                        {submitButtonText}
                      </button>
                    )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }
}

Wizard.propTypes = {
  initialValues: PropTypes.shape({}).isRequired,
  onSubmit: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([PropTypes.objectOf(Wizard.Page), PropTypes.arrayOf(Wizard.Page)]).isRequired,
  withSaveOption: PropTypes.bool,
  saveOptionText: PropTypes.string,
  cancelRequest: PropTypes.func,
  resumeCheckbox: PropTypes.func,
  enableReinitialize: PropTypes.bool,
  autoSave: PropTypes.bool,
  onPageChange: PropTypes.func,
};

const mapStateToProps = (state) => {
  return {
    page: getCurrentPage(state),
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...WizardActions,
    },
    dispatch
  );

export default compose(withNamespaces(), connect(mapStateToProps, mapDispatchToProps))(Wizard);
